import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Alert,
  CircularProgress,
  Button,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';
import AppearanceManager from '../components/admin/AppearanceManager';
import DomainManager from '../components/admin/DomainManager';
import UserManager from '../components/admin/UserManager';
import WorkFunctionManager from '../components/admin/WorkFunctionManager';
import PaymentTermsManager from '../components/admin/PaymentTermsManager';
import ActivityAuditLog from '../components/admin/ActivityAuditLog';
import KioskAnnouncementsManager from '../components/admin/KioskAnnouncementsManager';
import PartCategoriesManager from '../components/admin/PartCategoriesManager';
import PartLabelsManager from '../components/admin/PartLabelsManager';
import { employeeService } from '../services/employeeService';

const formatBytes = (bytes) => {
  const num = Number(bytes);
  if (Number.isNaN(num) || num < 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = num;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(2)} ${units[unitIndex]}`;
};

const Admin = () => {
  const { user, isAdmin } = useAuth();
  const [storage, setStorage] = React.useState(null);
  const [storageLoading, setStorageLoading] = React.useState(true);
  const [storageError, setStorageError] = React.useState('');
  const [attachments, setAttachments] = React.useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = React.useState(true);
  const [attachmentsError, setAttachmentsError] = React.useState('');
  const [attachmentsPage, setAttachmentsPage] = React.useState(1);
  const [attachmentsTotal, setAttachmentsTotal] = React.useState(0);
  const [attachmentsPageSize] = React.useState(25);
  const [packingSlipRecipientsInput, setPackingSlipRecipientsInput] = React.useState('');
  const [packingSlipRecipientsSaving, setPackingSlipRecipientsSaving] = React.useState(false);
  const [packingSlipRecipientsError, setPackingSlipRecipientsError] = React.useState('');
  const [packingSlipEmailLogs, setPackingSlipEmailLogs] = React.useState([]);
  const [packingSlipEmailLogsLoading, setPackingSlipEmailLogsLoading] = React.useState(true);
  const [packingSlipEmailLogsError, setPackingSlipEmailLogsError] = React.useState('');
  const [receiptNotifyRecipientsInput, setReceiptNotifyRecipientsInput] = React.useState('');
  const [receiptNotifyRecipientsSaving, setReceiptNotifyRecipientsSaving] = React.useState(false);
  const [receiptNotifyRecipientsError, setReceiptNotifyRecipientsError] = React.useState('');
  const [transportNotifyRecipientsInput, setTransportNotifyRecipientsInput] = React.useState('');
  const [transportNotifyRecipientsSaving, setTransportNotifyRecipientsSaving] = React.useState(false);
  const [transportNotifyRecipientsError, setTransportNotifyRecipientsError] = React.useState('');
  const [transportPaymentNotifyInput, setTransportPaymentNotifyInput] = React.useState('');
  const [transportPaymentNotifySaving, setTransportPaymentNotifySaving] = React.useState(false);
  const [transportPaymentNotifyError, setTransportPaymentNotifyError] = React.useState('');
  const [transportPaymentUsingFallback, setTransportPaymentUsingFallback] = React.useState(false);
  const [salesExpenseSupervisorId, setSalesExpenseSupervisorId] = React.useState('');
  const [salesExpenseSupervisorSaving, setSalesExpenseSupervisorSaving] = React.useState(false);
  const [salesExpenseSupervisorError, setSalesExpenseSupervisorError] = React.useState('');
  const [salesExpenseSupervisorEmployees, setSalesExpenseSupervisorEmployees] = React.useState([]);
  const [backupsInfo, setBackupsInfo] = React.useState(null);
  const [backupsLoading, setBackupsLoading] = React.useState(true);
  const [backupsError, setBackupsError] = React.useState('');

  React.useEffect(() => {
    const loadStorage = async () => {
      try {
        setStorageLoading(true);
        setStorageError('');
        const res = await axios.get('/api/admin/storage/attachments');
        const data = res?.data?.data ?? res?.data;
        setStorage(data || null);
      } catch (err) {
        console.error('Failed to load attachment storage stats:', err);
        setStorageError(err.response?.data?.message || 'Failed to load attachment storage stats');
      } finally {
        setStorageLoading(false);
      }
    };

    const loadPackingSlipSettings = async () => {
      try {
        setPackingSlipRecipientsError('');
        const res = await axios.get('/api/admin/settings/packing-slip-notifications');
        const data = res?.data?.data ?? res?.data;
        const listStr = Array.isArray(data?.recipients)
          ? data.recipients.join(', ')
          : (typeof data === 'string' ? data : '');
        setPackingSlipRecipientsInput(listStr);
      } catch (err) {
        console.error('Failed to load packing slip notification settings:', err);
        setPackingSlipRecipientsError(err.response?.data?.message || 'Failed to load packing slip notification settings');
      }
    };

    const loadReceiptNotifySettings = async () => {
      try {
        setReceiptNotifyRecipientsError('');
        const res = await axios.get('/api/admin/settings/expense-reimbursement-notifications');
        const data = res?.data?.data ?? res?.data;
        const listStr = Array.isArray(data?.recipients)
          ? data.recipients.join(', ')
          : (typeof data === 'string' ? data : '');
        setReceiptNotifyRecipientsInput(listStr);
      } catch (err) {
        console.error('Failed to load expense reimbursement notification settings:', err);
        setReceiptNotifyRecipientsError(
          err.response?.data?.message || 'Failed to load expense reimbursement notification settings'
        );
      }
    };

    const loadSalesExpenseSupervisor = async () => {
      try {
        setSalesExpenseSupervisorError('');
        const [settingRes, employeesRes] = await Promise.all([
          axios.get('/api/admin/settings/sales-expense-approval-supervisor'),
          employeeService.getAll({ activeOnly: 'true', limit: 500 }),
        ]);
        const data = settingRes?.data?.data ?? settingRes?.data;
        setSalesExpenseSupervisorId(
          data?.employee_id != null && data.employee_id !== '' ? String(data.employee_id) : ''
        );
        setSalesExpenseSupervisorEmployees(employeesRes?.data || []);
      } catch (err) {
        console.error('Failed to load sales expense approval supervisor:', err);
        setSalesExpenseSupervisorError(
          err.response?.data?.message || 'Failed to load sales expense approval supervisor'
        );
      }
    };

    const loadPackingSlipEmailLogs = async () => {
      try {
        setPackingSlipEmailLogsLoading(true);
        setPackingSlipEmailLogsError('');
        const res = await axios.get('/api/admin/settings/packing-slip-notifications/logs', { params: { limit: 20 } });
        const data = res?.data?.data ?? res?.data;
        setPackingSlipEmailLogs(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to load packing slip email logs:', err);
        setPackingSlipEmailLogsError(err.response?.data?.message || 'Failed to load packing slip email logs');
      } finally {
        setPackingSlipEmailLogsLoading(false);
      }
    };

    const loadBackups = async () => {
      try {
        setBackupsLoading(true);
        setBackupsError('');
        const res = await axios.get('/api/admin/storage/backups', { params: { limit: 100 } });
        const data = res?.data?.data ?? res?.data;
        setBackupsInfo(data || null);
      } catch (err) {
        console.error('Failed to load backups info:', err);
        setBackupsError(err.response?.data?.message || 'Failed to load backups info');
      } finally {
        setBackupsLoading(false);
      }
    };

    const loadTransportNotifySettings = async () => {
      try {
        setTransportNotifyRecipientsError('');
        const res = await axios.get('/api/admin/settings/transportation-job-notifications');
        const data = res?.data?.data ?? res?.data;
        const listStr = Array.isArray(data?.recipients)
          ? data.recipients.join(', ')
          : (typeof data === 'string' ? data : '');
        setTransportNotifyRecipientsInput(listStr);
      } catch (err) {
        console.error('Failed to load transportation notification settings:', err);
        setTransportNotifyRecipientsError(
          err.response?.data?.message || 'Failed to load transportation notification settings'
        );
      }
    };

    const loadTransportPaymentNotifySettings = async () => {
      try {
        setTransportPaymentNotifyError('');
        const res = await axios.get('/api/admin/settings/transportation-payment-notifications');
        const data = res?.data?.data ?? res?.data;
        const listStr = Array.isArray(data?.recipients)
          ? data.recipients.join(', ')
          : (typeof data === 'string' ? data : '');
        setTransportPaymentNotifyInput(listStr);
        setTransportPaymentUsingFallback(Boolean(res?.data?.using_expense_fallback));
      } catch (err) {
        console.error('Failed to load transportation payment notification settings:', err);
        setTransportPaymentNotifyError(
          err.response?.data?.message || 'Failed to load transportation payment notification settings'
        );
      }
    };

    if (isAdmin()) {
      loadStorage();
      loadPackingSlipSettings();
      loadReceiptNotifySettings();
      loadTransportNotifySettings();
      loadTransportPaymentNotifySettings();
      loadSalesExpenseSupervisor();
      loadPackingSlipEmailLogs();
      loadBackups();
      // Initial attachments load
      (async () => {
        try {
          setAttachmentsLoading(true);
          setAttachmentsError('');
          const res = await axios.get('/api/admin/storage/attachments/list', {
            params: { page: 1, pageSize: attachmentsPageSize }
          });
          const data = res?.data?.data ?? res?.data;
          setAttachments(data?.items || []);
          setAttachmentsTotal(data?.pagination?.total || 0);
          setAttachmentsPage(data?.pagination?.page || 1);
        } catch (err) {
          console.error('Failed to load attachments list:', err);
          setAttachmentsError(err.response?.data?.message || 'Failed to load attachments list');
        } finally {
          setAttachmentsLoading(false);
        }
      })();
    }
  }, [isAdmin]);

  // Only allow admin users to access this page
  if (!isAdmin()) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access denied. Admin privileges required.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Admin Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Manage system settings and configurations
      </Typography>

      <Grid container spacing={3}>
        {/* Appearance / theme */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Appearance"
              subheader="Choose the look and feel used across WorkManager"
            />
            <Divider />
            <CardContent>
              <AppearanceManager />
            </CardContent>
          </Card>
        </Grid>

        {/* User Management */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="User Management"
              subheader="Manage employees, managers, and administrators"
            />
            <Divider />
            <CardContent>
              <UserManager />
            </CardContent>
          </Card>
        </Grid>

        {/* Activity audit log */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Activity audit log"
              subheader="Who signed in or out, clock punches, and authenticated saves while signed in"
            />
            <Divider />
            <CardContent>
              <ActivityAuditLog />
            </CardContent>
          </Card>
        </Grid>

        {/* Domain Management */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Domain Management"
              subheader="Configure domains that can access this FAB WORKS instance"
            />
            <Divider />
            <CardContent>
              <DomainManager />
            </CardContent>
          </Card>
        </Grid>

        {/* System Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="System Information" />
            <Divider />
            <CardContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Current User
                </Typography>
                <Typography variant="body1">
                  {user?.first_name} {user?.last_name} ({user?.email})
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Role
                </Typography>
                <Typography variant="body1">
                  {user?.role}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Current Domain
                </Typography>
                <Typography variant="body1">
                  {typeof window !== 'undefined' ? window.location.origin : 'N/A'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Credit / payment terms (customer & invoice dropdowns) */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title="Credit terms (payment terms)"
              subheader="Options for customer credit and invoice payment terms (e.g. Net 30, Net 20)"
            />
            <Divider />
            <CardContent>
              <PaymentTermsManager />
            </CardContent>
          </Card>
        </Grid>

        {/* Part categories */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title="Part categories"
              subheader="Manage category names used on the Parts list. Removing a category clears it on parts that used it."
            />
            <Divider />
            <CardContent>
              <PartCategoriesManager />
            </CardContent>
          </Card>
        </Grid>

        {/* Part labels */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title="Part labels"
              subheader="Colored chips assigned to parts. Removing a label unassigns it from all parts."
            />
            <Divider />
            <CardContent>
              <PartLabelsManager />
            </CardContent>
          </Card>
        </Grid>

        {/* Packing slip notifications (default recipients) */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Packing Slip Notifications" />
            <Divider />
            <CardContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Default email recipients for packing slip notifications. Customers can override this list individually.
              </Typography>
              {packingSlipRecipientsError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {packingSlipRecipientsError}
                </Alert>
              )}
              <TextField
                fullWidth
                size="small"
                label="Default recipient emails (comma-separated)"
                value={packingSlipRecipientsInput}
                onChange={(e) => setPackingSlipRecipientsInput(e.target.value)}
                sx={{ mb: 1 }}
              />
              <Button
                variant="contained"
                size="small"
                disabled={packingSlipRecipientsSaving}
                onClick={async () => {
                  try {
                    setPackingSlipRecipientsSaving(true);
                    setPackingSlipRecipientsError('');
                    const recipients = packingSlipRecipientsInput
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean);
                    await axios.put('/api/admin/settings/packing-slip-notifications', {
                      recipients
                    });
                  } catch (err) {
                    console.error('Failed to save packing slip notification settings:', err);
                    setPackingSlipRecipientsError(err.response?.data?.message || 'Failed to save settings');
                  } finally {
                    setPackingSlipRecipientsSaving(false);
                  }
                }}
              >
                {packingSlipRecipientsSaving ? 'Saving…' : 'Save'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Transportation job approval notifications */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Transportation Job Notifications" />
            <Divider />
            <CardContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Email addresses notified when a driver marks a transportation job complete and it is
                waiting for office approval.
              </Typography>
              {transportNotifyRecipientsError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {transportNotifyRecipientsError}
                </Alert>
              )}
              <TextField
                fullWidth
                size="small"
                label="Recipient emails (comma-separated)"
                value={transportNotifyRecipientsInput}
                onChange={(e) => setTransportNotifyRecipientsInput(e.target.value)}
                sx={{ mb: 1 }}
              />
              <Button
                variant="contained"
                size="small"
                disabled={transportNotifyRecipientsSaving}
                onClick={async () => {
                  try {
                    setTransportNotifyRecipientsSaving(true);
                    setTransportNotifyRecipientsError('');
                    const recipients = transportNotifyRecipientsInput
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean);
                    await axios.put('/api/admin/settings/transportation-job-notifications', {
                      recipients,
                    });
                  } catch (err) {
                    console.error('Failed to save transportation notification settings:', err);
                    setTransportNotifyRecipientsError(
                      err.response?.data?.message || 'Failed to save settings'
                    );
                  } finally {
                    setTransportNotifyRecipientsSaving(false);
                  }
                }}
              >
                {transportNotifyRecipientsSaving ? 'Saving…' : 'Save'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Transportation payment / final receipt notifications */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Transportation Payment Notifications" />
            <Divider />
            <CardContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Email addresses notified when a transportation job is <strong>approved</strong> (final
                receipt with photos and documents for payment / billing). Separate from expense
                reimbursement notifications.
              </Typography>
              {transportPaymentUsingFallback && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  Not configured yet — approved-job receipts currently still go to Expense
                  Reimbursement Notifications until you save recipients here.
                </Alert>
              )}
              {transportPaymentNotifyError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {transportPaymentNotifyError}
                </Alert>
              )}
              <TextField
                fullWidth
                size="small"
                label="Recipient emails (comma-separated)"
                value={transportPaymentNotifyInput}
                onChange={(e) => setTransportPaymentNotifyInput(e.target.value)}
                sx={{ mb: 1 }}
              />
              <Button
                variant="contained"
                size="small"
                disabled={transportPaymentNotifySaving}
                onClick={async () => {
                  try {
                    setTransportPaymentNotifySaving(true);
                    setTransportPaymentNotifyError('');
                    const recipients = transportPaymentNotifyInput
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean);
                    await axios.put('/api/admin/settings/transportation-payment-notifications', {
                      recipients,
                    });
                    setTransportPaymentUsingFallback(false);
                  } catch (err) {
                    console.error('Failed to save transportation payment notification settings:', err);
                    setTransportPaymentNotifyError(
                      err.response?.data?.message || 'Failed to save settings'
                    );
                  } finally {
                    setTransportPaymentNotifySaving(false);
                  }
                }}
              >
                {transportPaymentNotifySaving ? 'Saving…' : 'Save'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Expense reimbursement (kiosk receipt) notifications */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Expense Reimbursement Notifications" />
            <Divider />
            <CardContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Email addresses notified when a reimbursement is <strong>approved</strong> (with the
                receipt attached). Recipients can mark reimbursements Paid via the REIMBURSED button.
                If no recipients are configured here, no notification email is sent.
              </Typography>
              {receiptNotifyRecipientsError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {receiptNotifyRecipientsError}
                </Alert>
              )}
              <TextField
                fullWidth
                size="small"
                label="Recipient emails (comma-separated)"
                value={receiptNotifyRecipientsInput}
                onChange={(e) => setReceiptNotifyRecipientsInput(e.target.value)}
                sx={{ mb: 1 }}
              />
              <Button
                variant="contained"
                size="small"
                disabled={receiptNotifyRecipientsSaving}
                onClick={async () => {
                  try {
                    setReceiptNotifyRecipientsSaving(true);
                    setReceiptNotifyRecipientsError('');
                    const recipients = receiptNotifyRecipientsInput
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean);
                    await axios.put('/api/admin/settings/expense-reimbursement-notifications', {
                      recipients,
                    });
                  } catch (err) {
                    console.error('Failed to save expense reimbursement notification settings:', err);
                    setReceiptNotifyRecipientsError(
                      err.response?.data?.message || 'Failed to save settings'
                    );
                  } finally {
                    setReceiptNotifyRecipientsSaving(false);
                  }
                }}
              >
                {receiptNotifyRecipientsSaving ? 'Saving…' : 'Save'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Sales Expense Approval Supervisor */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Sales Expense Approval Supervisor" />
            <Divider />
            <CardContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Employee who receives an email when a Sales Portal expense is submitted, and who can
                approve or reject those expenses in the Sales Portal. Managers and admins can also
                approve. The selected person must have an email on file.
              </Typography>
              {salesExpenseSupervisorError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {salesExpenseSupervisorError}
                </Alert>
              )}
              <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                <InputLabel id="sales-expense-supervisor-label">Supervisor</InputLabel>
                <Select
                  labelId="sales-expense-supervisor-label"
                  label="Supervisor"
                  value={salesExpenseSupervisorId}
                  onChange={(e) => setSalesExpenseSupervisorId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {salesExpenseSupervisorEmployees.map((emp) => {
                    const name = `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || `#${emp.id}`;
                    const email = emp.email ? ` — ${emp.email}` : ' — no email';
                    return (
                      <MenuItem key={emp.id} value={String(emp.id)} disabled={!emp.email}>
                        {name}{email}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                size="small"
                disabled={salesExpenseSupervisorSaving}
                onClick={async () => {
                  try {
                    setSalesExpenseSupervisorSaving(true);
                    setSalesExpenseSupervisorError('');
                    await axios.put('/api/admin/settings/sales-expense-approval-supervisor', {
                      employee_id: salesExpenseSupervisorId === '' ? null : Number(salesExpenseSupervisorId),
                    });
                  } catch (err) {
                    console.error('Failed to save sales expense approval supervisor:', err);
                    setSalesExpenseSupervisorError(
                      err.response?.data?.message || 'Failed to save settings'
                    );
                  } finally {
                    setSalesExpenseSupervisorSaving(false);
                  }
                }}
              >
                {salesExpenseSupervisorSaving ? 'Saving…' : 'Save'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <KioskAnnouncementsManager />
        </Grid>

        {/* Recent sent email logs */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Sent Packing Slip Emails (Last 20)" />
            <Divider />
            <CardContent>
              {packingSlipEmailLogsError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {packingSlipEmailLogsError}
                </Alert>
              )}

              {packingSlipEmailLogsLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    Loading…
                  </Typography>
                </Box>
              ) : packingSlipEmailLogs.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No email logs yet.
                </Typography>
              ) : (
                <Box sx={{ maxHeight: 360, overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>
                          Time
                        </th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>
                          Status
                        </th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>
                          Customer
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {packingSlipEmailLogs.map((log) => {
                        const deleted = !!log.slip_deleted;
                        const rowStyle = deleted
                          ? { color: '#9e9e9e', background: '#f5f5f5' }
                          : undefined;
                        return (
                          <tr key={log.id} style={rowStyle}>
                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0', whiteSpace: 'nowrap' }}>
                              {log.created_at ? new Date(log.created_at).toLocaleString() : '—'}
                            </td>
                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0', whiteSpace: 'nowrap' }}>
                              {log.status || '—'}
                              {deleted ? ' · slip deleted' : ''}
                            </td>
                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                              <div style={{ fontWeight: 600 }}>{log.customer_name || '—'}</div>
                              <div style={{ color: deleted ? '#9e9e9e' : '#666', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {log.packing_slip_identifier ? `${log.packing_slip_identifier} · ` : ''}
                                {log.subject || ''}
                              </div>
                              {log.error ? (
                                <div style={{ color: 'crimson', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={log.error}>
                                  {log.error}
                                </div>
                              ) : null}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Function management (work_functions) */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Functions" subheader="Configure the Function dropdown in Time Entries" />
            <Divider />
            <CardContent>
              <WorkFunctionManager hideHeader />
            </CardContent>
          </Card>
        </Grid>

        {/* Attachments / Uploads Storage */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Uploads / Attachments Storage"
              subheader="Monitor disk usage for the attachments directory on the server"
            />
            <Divider />
            <CardContent>
              {storageLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Loading storage stats…</Typography>
                </Box>
              ) : storageError ? (
                <Alert severity="error">{storageError}</Alert>
              ) : !storage ? (
                <Typography variant="body2" color="text.secondary">
                  Storage information is not available.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  {storage.storage_type === 's3' && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Storage
                      </Typography>
                      <Typography variant="body2">S3</Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      {storage.storage_type === 's3' ? 'S3 location' : 'Attachments directory'}
                    </Typography>
                    <Typography variant="body2">
                      {storage.base_dir || '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Files
                    </Typography>
                    <Typography variant="body2">
                      {storage.files?.count ?? 0} file(s)
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Space Used by Attachments
                    </Typography>
                    <Typography variant="body2">
                      {formatBytes(storage.files?.total_bytes ?? 0)}
                    </Typography>
                  </Box>
                  {storage.disk && storage.disk.total_bytes != null && (
                    <>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Disk Total
                        </Typography>
                        <Typography variant="body2">
                          {formatBytes(storage.disk.total_bytes)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Disk Available
                        </Typography>
                        <Typography variant="body2">
                          {formatBytes(storage.disk.available_bytes)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Filesystem / Mount
                        </Typography>
                        <Typography variant="body2">
                          {storage.disk.filesystem} ({storage.disk.mount_point})
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Attachments File Manager */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Attachments File Manager"
              subheader="Browse uploaded attachments with customer, quote, and order context"
            />
            <Divider />
            <CardContent>
              {attachmentsError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {attachmentsError}
                </Alert>
              )}
              {attachmentsLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Loading attachments…</Typography>
                </Box>
              ) : attachments.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No attachments found.
                </Typography>
              ) : (
                <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>File</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>Customer</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>Quote</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>Order</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>Size</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>Uploaded</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>By</th>
                        <th style={{ width: 48 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {attachments.map((att) => (
                        <tr key={att.id}>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                            {att.file_name}
                          </td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                            {att.customer_id ? (
                              <Link
                                to={`/customers/${att.customer_id}`}
                                style={{ color: 'inherit', textDecoration: 'underline' }}
                              >
                                {att.customer_number != null
                                  ? `${att.customer_number} – ${att.company_name || ''}`
                                  : att.company_name || `Customer #${att.customer_id}`}
                              </Link>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                            {att.quote_id ? (
                              <Link to={`/quotes/${att.quote_id}`} style={{ color: 'inherit', textDecoration: 'underline' }}>
                                {att.quote_identifier != null ? att.quote_identifier : att.quote_number != null ? att.quote_number : att.quote_id}
                              </Link>
                            ) : '—'}
                          </td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                            {att.work_order_id ? (
                              <Link to={`/work-orders/${att.work_order_id}`} style={{ color: 'inherit', textDecoration: 'underline' }}>
                                {att.work_order_identifier != null ? att.work_order_identifier : att.work_order_number != null ? att.work_order_number : att.work_order_id}
                              </Link>
                            ) : '—'}
                          </td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                            {formatBytes(att.file_size || 0)}
                          </td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                            {att.uploaded_at
                              ? new Date(att.uploaded_at).toLocaleString()
                              : '—'}
                          </td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                            {att.uploaded_by_first_name || att.uploaded_by_last_name
                              ? `${att.uploaded_by_first_name || ''} ${att.uploaded_by_last_name || ''}`.trim()
                              : '—'}
                          </td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                            <IconButton
                              size="small"
                              color="error"
                              aria-label="Delete attachment"
                              onClick={async () => {
                                if (!window.confirm(`Delete attachment "${att.file_name}"? This cannot be undone.`)) return;
                                try {
                                  await axios.delete(`/api/attachments/${att.id}`);
                                  setAttachments((prev) => prev.filter((x) => x.id !== att.id));
                                  setAttachmentsTotal((prev) => Math.max(prev - 1, 0));
                                  try {
                                    const storageRes = await axios.get('/api/admin/storage/attachments');
                                    const storageData = storageRes?.data?.data ?? storageRes?.data;
                                    setStorage(storageData || null);
                                  } catch (err) {
                                    console.error('Failed to refresh storage stats after delete:', err);
                                  }
                                } catch (err) {
                                  console.error('Failed to delete attachment:', err);
                                  setAttachmentsError(err.response?.data?.message || 'Failed to delete attachment');
                                }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Showing {(attachmentsPage - 1) * attachmentsPageSize + 1}–
                      {Math.min(attachmentsPage * attachmentsPageSize, attachmentsTotal)} of {attachmentsTotal}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={attachmentsPage <= 1}
                        onClick={async () => {
                          if (attachmentsPage <= 1) return;
                          const newPage = attachmentsPage - 1;
                          try {
                            setAttachmentsLoading(true);
                            const res = await axios.get('/api/admin/storage/attachments/list', {
                              params: { page: newPage, pageSize: attachmentsPageSize }
                            });
                            const data = res?.data?.data ?? res?.data;
                            setAttachments(data?.items || []);
                            setAttachmentsTotal(data?.pagination?.total || 0);
                            setAttachmentsPage(data?.pagination?.page || newPage);
                          } catch (err) {
                            console.error('Failed to load attachments list:', err);
                            setAttachmentsError(err.response?.data?.message || 'Failed to load attachments list');
                          } finally {
                            setAttachmentsLoading(false);
                          }
                        }}
                      >
                        Previous
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={attachmentsPage * attachmentsPageSize >= attachmentsTotal}
                        onClick={async () => {
                          if (attachmentsPage * attachmentsPageSize >= attachmentsTotal) return;
                          const newPage = attachmentsPage + 1;
                          try {
                            setAttachmentsLoading(true);
                            const res = await axios.get('/api/admin/storage/attachments/list', {
                              params: { page: newPage, pageSize: attachmentsPageSize }
                            });
                            const data = res?.data?.data ?? res?.data;
                            setAttachments(data?.items || []);
                            setAttachmentsTotal(data?.pagination?.total || 0);
                            setAttachmentsPage(data?.pagination?.page || newPage);
                          } catch (err) {
                            console.error('Failed to load attachments list:', err);
                            setAttachmentsError(err.response?.data?.message || 'Failed to load attachments list');
                          } finally {
                            setAttachmentsLoading(false);
                          }
                        }}
                      >
                        Next
                      </Button>
                    </Box>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Database Backups */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Database Backups"
              subheader="Backup schedule status and recent S3 backup files"
            />
            <Divider />
            <CardContent>
              {backupsLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Loading backups…</Typography>
                </Box>
              ) : backupsError ? (
                <Alert severity="error">{backupsError}</Alert>
              ) : !backupsInfo ? (
                <Typography variant="body2" color="text.secondary">
                  Backup information is not available.
                </Typography>
              ) : (
                <Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Bucket</Typography>
                      <Typography variant="body2">{backupsInfo.bucket || '—'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Prefix</Typography>
                      <Typography variant="body2">{backupsInfo.prefix || '—'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Retention</Typography>
                      <Typography variant="body2">Keep last {backupsInfo.retention?.keep_last_backups ?? 90}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Cron status</Typography>
                      <Typography variant="body2">
                        {backupsInfo.cron?.configured ? 'Configured' : 'Not detected'}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Expected schedule
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {backupsInfo.cron?.expected_schedule || '—'}
                  </Typography>
                  {backupsInfo.cron?.detected_line && (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Detected on server
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        {backupsInfo.cron.detected_line}
                      </Typography>
                    </>
                  )}

                  {Array.isArray(backupsInfo.backups) && backupsInfo.backups.length > 0 ? (
                    <Box sx={{ maxHeight: 360, overflow: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>File</th>
                            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>Size</th>
                            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>Last Modified</th>
                            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e0e0e0' }}>Checksum</th>
                          </tr>
                        </thead>
                        <tbody>
                          {backupsInfo.backups.map((b) => (
                            <tr key={b.key}>
                              <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>{b.filename || b.key}</td>
                              <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>{formatBytes(b.size_bytes || 0)}</td>
                              <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                                {b.last_modified ? new Date(b.last_modified).toLocaleString() : '—'}
                              </td>
                              <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                                {b.has_checksum ? 'Yes' : 'No'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No backups found in this S3 prefix yet.
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Admin;
